import { storage } from '../../utils/storage.js';
import { storageManager } from '../../../core/storage/index.js';
import { getMessageDisplayManager } from './message-display-manager.js';
import { isVnLayoutMode } from './layout-mode.js';
import { hasValidResponseActionId } from '../shared/sprite-state.js';
import { fetchCharacterSprite } from './character-manager.js';

export class ConversationQueue {
    constructor() {
        this.pendingResponses = [];
        this.isWaitingForUser = false;
        this._isManualPacing = this.getStoredPacingMode();
        this.streamingComplete = false;

        this.continueConversation = this.continueConversation.bind(this);
        this.setPacingMode = this.setPacingMode.bind(this);
        this.markStreamingComplete = this.markStreamingComplete.bind(this);
    }

    get isManualPacing() {
        if (!isVnLayoutMode()) return false;
        return this._isManualPacing;
    }

    set isManualPacing(value) {
        this._isManualPacing = value;
    }

    getStoredPacingMode() {
        if (!isVnLayoutMode()) return false;
        const stored = storage.get('manualCharacterPacing');
        return stored === null ? true : stored === 'true';
    }

    /**
     * @returns {boolean} True if response was queued
     */
    queueResponse(responseData, charResponse, finalText) {
        if (!this.isManualPacing) return false;
        if (!charResponse?.isQueued) return false;

        this.pendingResponses.push({
            responseData,
            streamingChunks: charResponse.streamingChunks || [],
            finalText,
            keepReasoningPreview: charResponse.reasoningExhausted === true && Boolean(charResponse.reasoning),
            timestamp: Date.now()
        });

        this.setWaitingState(true);
        return true;
    }

    /**
     * @returns {boolean} True if a response was displayed
     */
    continueConversation() {
        if (this.pendingResponses.length === 0) return false;

        const next = this.pendingResponses.shift();
        this.displayQueuedResponse(next);

        if (this.pendingResponses.length === 0) {
            this.setWaitingState(false);
        }

        return true;
    }

    async displayQueuedResponse(queuedItem) {
        const { responseData, streamingChunks } = queuedItem;
        const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
        const characters = hangoutConfig?.allCharacters || [];
        const isMobilePortrait = window.innerWidth <= 1024 && window.innerHeight > window.innerWidth;

        let spriteUpdated = false;
        let bubbleCreated = false;

        try {
            if (isMobilePortrait) {
                const hangoutContainer = document.getElementById('hangout-container');
                if (hangoutContainer && document.querySelectorAll('.character-image').length > 1) {
                    hangoutContainer.classList.add('conversation-active');
                }

                const bubblesCanvas = document.getElementById('character-bubbles-canvas');
                if (bubblesCanvas) bubblesCanvas.classList.add('bubble-visible');

                const characterIndex = characters.findIndex(c => String(c.id) === String(responseData.characterId));
                if (characterIndex >= 0) {
                    const characterContainer = document.querySelector(`.character-image-${characterIndex}`);
                    if (characterContainer) {
                        characterContainer.classList.add('currently-speaking');
                        document.querySelectorAll('.character-image.currently-speaking, [class*="character-image-"].currently-speaking').forEach(el => {
                            if (el !== characterContainer) el.classList.remove('currently-speaking');
                        });
                    }
                }
            }

            await this.updateCharacterSpriteForQueuedResponse(responseData, characters);
            spriteUpdated = true;

            const displayManager = await getMessageDisplayManager();
            const currentMessages = displayManager.getActiveMessages();
            let bubble = currentMessages.find(b => String(b.characterId) === String(responseData.characterId));

            if (bubble && bubble.isTyping) {
                bubble.isTyping = false;
                bubble.isAwaitingDecision = false;
                if (typeof bubble.setText === 'function') bubble.setText('');
                if (typeof bubble.markDirty === 'function') bubble.markDirty();
                bubble.voiceButtonState = 'play';
                bubble.isAudioPlaying = false;
                bubble.currentVoiceKey = null;
            } else {
                bubble = await displayManager.createTypingIndicator(responseData.characterId, responseData.characterName);
                if (bubble) {
                    if (typeof bubble.markDirty === 'function') bubble.markDirty();
                    displayManager.redrawAllMessages();
                    await new Promise(resolve => setTimeout(resolve, 100));
                    bubble.isTyping = false;
                    bubble.isAwaitingDecision = false;
                    if (typeof bubble.setText === 'function') bubble.setText('');
                    bubble.voiceButtonState = 'play';
                    bubble.isAudioPlaying = false;
                    bubble.currentVoiceKey = null;
                }
            }

            bubbleCreated = true;

            if (window.preGeneratedAudioMap && window.preGeneratedAudioMap.has(responseData.characterId)) {
                bubble.setVoiceButtonState('ready');
                const messageText = (streamingChunks || []).join('');
                const voiceStateKey = `voice_ready_${responseData.characterId}_${btoa(unescape(encodeURIComponent(messageText))).slice(0, 50)}`;
                try {
                    storage.set(voiceStateKey, 'ready');
                } catch {
                    // storage quota
                }
            }

            await this.streamQueuedContent(bubble, streamingChunks || []);
            if (queuedItem.keepReasoningPreview) {
                bubble.isReasoningPreview = true;
                bubble.setReasoningPreview?.(true);
            }

            if (isVnLayoutMode()) {
                try {
                    const { refreshVnTurnNavigationFromLatestRound } = await import('./message-utils.js');
                    await refreshVnTurnNavigationFromLatestRound(responseData.characterId, {
                        preserveUserNavigation: true
                    });
                } catch (navError) {
                    console.warn('Failed to refresh VN turn navigation after queued response:', navError);
                }
            }

            await this.performCleanupIfReady();
        } catch (error) {
            console.error('Error displaying queued response:', error);
            if (spriteUpdated && !bubbleCreated && isMobilePortrait) {
                document.querySelectorAll('.currently-speaking').forEach(el => {
                    el.classList.remove('currently-speaking');
                });
            }
        }
    }

    async updateCharacterSpriteForQueuedResponse(responseData, characters) {
        try {
            if (!hasValidResponseActionId(responseData.actionId)) return;

            const characterList = characters || [];
            const character = characterList.find(c => String(c.id) === String(responseData.characterId));
            const characterIndex = characterList.findIndex(c => String(c.id) === String(responseData.characterId));
            if (!character) return;

            const { saveAllZoomStates } = await import('./imageZoom.js');
            saveAllZoomStates();

            const isVideo = responseData.isVideo === true || responseData.is_video === true;
            await fetchCharacterSprite(
                responseData.characterId,
                responseData.actionId,
                responseData.outfitId ?? character.outfitId ?? 0,
                Math.max(0, characterIndex),
                true,
                isVideo
            );
        } catch (error) {
            console.warn('Error updating sprite for queued response:', error);
        }
    }

    async streamQueuedContent(bubble, chunks) {
        if (!bubble || !chunks.length) return;

        let currentContent = '';

        for (const chunk of chunks) {
            const isCJKChunk = this.isCJKText(chunk);

            if (isCJKChunk) {
                for (const char of chunk) {
                    currentContent += char;
                    bubble.setText(currentContent);
                    await new Promise(resolve => setTimeout(resolve, 40));
                }
            } else {
                currentContent += chunk;
                bubble.setText(currentContent);
                await new Promise(resolve => setTimeout(resolve, 60));
            }
        }
    }

    isCJKText(text) {
        const cjkRegex = /[\u3040-\u309f\u30a0-\u30ff\u4e00-\u9fff\uac00-\ud7af]/;
        return cjkRegex.test(text);
    }

    async setPacingMode(isManual) {
        const wasManual = this.isManualPacing;
        this.isManualPacing = isManual;

        try {
            storage.set('manualCharacterPacing', isManual.toString());
        } catch (error) {
            console.warn('Failed to persist pacing mode to storage:', error);
        }

        if (wasManual && !isManual) {
            await this.flushQueue();
        }
    }

    async flushQueue() {
        if (this.pendingResponses.length === 0) return;

        while (this.pendingResponses.length > 0) {
            const next = this.pendingResponses.shift();
            await this.displayQueuedResponse(next);
        }

        this.setWaitingState(false);
    }

    setWaitingState(isWaiting) {
        this.isWaitingForUser = isWaiting;
    }

    async markStreamingComplete() {
        this.streamingComplete = true;
        if (this.pendingResponses.length === 0) {
            await this.performCleanupIfReady();
        }
    }

    async performCleanupIfReady() {
        if (!this.streamingComplete) return false;
        if (this.pendingResponses.length > 0) return false;
        await this.performMobileCleanup();
        return true;
    }

    async performMobileCleanup() {
        const isMobileViewport = window.innerWidth <= 1024;
        if (!isMobileViewport) return;

        const multiCharContainers = document.querySelectorAll('[class*="character-image-"]');
        const isMobilePortrait = window.innerWidth <= 1024 && window.innerHeight > window.innerWidth;
        const isMultiCharMobilePortrait = isMobilePortrait && multiCharContainers.length > 1;

        if (isMultiCharMobilePortrait) return;

        const hangoutContainer = document.getElementById('hangout-container');
        if (hangoutContainer) {
            hangoutContainer.classList.remove('conversation-active');
            hangoutContainer.classList.remove('single-character-conversation');
        }

        document.querySelectorAll('.currently-speaking').forEach(el => {
            el.classList.remove('currently-speaking');
        });
    }

    clearQueue() {
        if (this.pendingResponses.length > 0) {
            this.pendingResponses = [];
            this.setWaitingState(false);
        }
        this.streamingComplete = false;
    }
}

export const conversationQueue = new ConversationQueue();

window.conversationQueue = conversationQueue;
